import Select from '@/components/form/select';
import Text from '@/components/text';
import { FormHandle } from '@/hooks/use-form-handle.hook';
import { PolicyModelV1 } from '@/lib/models/policy-model-v1/policy-v1.model';
import { Box, Stack, Theme } from '@mui/material';
import { cloneDeep } from 'lodash';
import { FC, useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { getFormattedUiOptions } from '../../select/helpers';
import {
  ContentControlSettings,
  ContentControlType,
  getContentControlActionOptions,
} from '@/lib/models/policy-model-v1/content-controls';
import { ControlActionValue } from '@/lib/models/policy-model-v1/controls';

interface Props {
  policy: PolicyModelV1;
  orgPolicy?: PolicyModelV1;
  formHandle: FormHandle<PolicyModelV1>;
  readonly: boolean;
  controlType: ContentControlType;
}

const useStyles = createUseStyles((theme: Theme) => ({
  contentControlItem: {
    '&:not(:last-child)': {
      borderBottom: `1px solid ${theme.palette.divider}`,
      marginBottom: 8,
      paddingBottom: 8,
    },
  },
}));

const ContentControlItemV1: FC<Props> = ({
  controlType,
  formHandle,
  readonly,
  policy,
  orgPolicy,
}) => {
  const styles = useStyles();

  const { values, setFieldValue } = formHandle;

  const handleChange = (
    controlType: ContentControlType,
    entityIndex: number,
    action: ControlActionValue
  ) => {
    const updated = values.contentControls.entities
      ? cloneDeep(values.contentControls.entities)
      : [];

    if (entityIndex > -1) {
      updated[entityIndex].action = action;
    } else {
      updated.push({ type: controlType, action });
    }
    setFieldValue('contentControls.entities', updated);
  };

  const actionOptionsMap = useMemo(
    () => getContentControlActionOptions(policy, orgPolicy),
    [policy, orgPolicy]
  );

  const [contentControl, entityIndex, entity, entityActionOptions] = useMemo(() => {
    const contentControl = ContentControlSettings.find(({ type }) => type === controlType)!;
    const entityIndex = values.contentControls.entities.findIndex(
      ({ type }) => type === controlType
    );
    const entity = entityIndex > -1 ? values.contentControls.entities[entityIndex] : null;
    const entityActionOptions = getFormattedUiOptions(actionOptionsMap.get(controlType) || []);

    return [contentControl, entityIndex, entity, entityActionOptions];
  }, [values.contentControls.entities, controlType, actionOptionsMap]);

  const { label, description, defaultAction } = contentControl;

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      className={styles.contentControlItem}
    >
      <Box pr={4}>
        <Text>{label}</Text>
        <Box mt={0.5}>
          <Text size="small" color="grey">
            {description}
          </Text>
        </Box>
      </Box>
      <Box minWidth={250}>
        <Select
          name={`contentControls.${controlType}`}
          options={entityActionOptions}
          readonly={readonly}
          value={entity?.action || defaultAction}
          onChange={(value: string) =>
            handleChange(controlType, entityIndex, value as ControlActionValue)
          }
          size="small"
        />
      </Box>
    </Stack>
  );
};

export default ContentControlItemV1;
